/**
 * FIX-EDIT-AFTER-LOCK (D-347 §4) - pure helpers of "בקשת שינוי" (no React, unit-tested).
 */

/** The server's refusal codes after which the employee is offered "בקשת שינוי" instead of only the error. */
export const CHANGE_REQUEST_LOCK_CODES: readonly string[] = ['PERIOD_LOCKED', 'SERVICE_LOG_LOCKED_EDIT', 'BILLED_MONTH_LOCKED'];

/** The machine code of an API refusal, whichever shape the client rejected with. */
export function errorCodeOf(err: unknown): string | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { error?: { error_code?: string }; response?: { data?: { error?: { error_code?: string } } } };
  return e.error?.error_code ?? e.response?.data?.error?.error_code;
}

/** Was this edit refused because the month is closed / a report was already produced on the log? */
export function isLockRefusal(err: unknown): boolean {
  const code = errorCodeOf(err);
  return !!code && CHANGE_REQUEST_LOCK_CODES.includes(code);
}

/** "YYYY-MM-DD" -> "DD.MM.YYYY" (display only). */
export function displayDate(d: string | null | undefined): string {
  if (!d || !/^\d{4}-\d{2}-\d{2}/.test(d)) return '';
  const [y, m, day] = d.slice(0, 10).split('-');
  return `${day}.${m}.${y}`;
}

/** HH:mm in Israel time of an instant (the payload keeps ISO instants). */
export function israelHm(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Jerusalem', hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
}

/** One readable line of WHAT the request changes (the request card, both sides). */
export function changeSummaryParts(r: { request_type: string; target_date?: string | null; payload: Record<string, any> }): {
  kind: 'attendance' | 'log_update' | 'log_delete' | 'log_move';
  date: string;
  from: string;
  to: string;
  breaks: number;
  summary: string;
  count: number;
} {
  const p = r.payload ?? {};
  if (r.request_type === 'attendance_day') {
    return {
      kind: 'attendance', date: displayDate(p.date ?? r.target_date), from: israelHm(p.clock_in), to: israelHm(p.clock_out),
      breaks: Array.isArray(p.breaks) ? p.breaks.length : 0, summary: '', count: 0,
    };
  }
  if (r.request_type === 'work_log_move') {
    return { kind: 'log_move', date: '', from: '', to: '', breaks: 0, summary: '', count: Array.isArray(p.workLogIds) ? p.workLogIds.length : 0 };
  }
  if (p.action === 'delete') return { kind: 'log_delete', date: '', from: '', to: '', breaks: 0, summary: '', count: 1 };
  return {
    kind: 'log_update', date: displayDate(typeof p.start_time === 'string' ? israelDate(p.start_time) : ''),
    from: israelHm(p.start_time), to: israelHm(p.end_time), breaks: 0, summary: typeof p.summary === 'string' ? p.summary : '', count: 1,
  };
}

function israelDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** The minimum reason length (same as the server). */
export const MIN_REASON = 3;
export const reasonOk = (s: string): boolean => s.trim().length >= MIN_REASON;

export type ChangeField = 'clockIn' | 'clockOut' | 'breaks' | 'date' | 'start' | 'end' | 'summary' | 'log';
/** One "לפני -> אחרי" line. `after` null = deleted. */
export interface ChangeRow { field: ChangeField; before: string; after: string | null }

/**
 * QA-EDIT-AFTER-LOCK #1 - the manager (and the employee) see exactly what changes: the values when the request
 * was sent (`before_values`) next to the requested ones (`payload`). Only fields that change are listed.
 */
export function changeRows(r: { request_type: string; payload: Record<string, any>; before_values?: Record<string, any> | null }): ChangeRow[] {
  const p = r.payload ?? {};
  const b = r.before_values ?? {};
  const out: ChangeRow[] = [];
  const push = (field: ChangeField, before: string, after: string | null) => { if (before !== after) out.push({ field, before: before || '—', after: after === null ? null : after || '—' }); };
  if (r.request_type === 'attendance_day') {
    push('clockIn', israelHm(b.clock_in), israelHm(p.clock_in));
    push('clockOut', israelHm(b.clock_out), israelHm(p.clock_out));
    push('breaks', String(b.breaks ?? '—'), String(Array.isArray(p.breaks) ? p.breaks.length : 0));
    return out;
  }
  if (r.request_type === 'work_log') {
    const span = (s: unknown, e: unknown) => `${displayDate(typeof s === 'string' ? israelDate(s) : '')} ${israelHm(s)}–${israelHm(e)}`.trim();
    if (p.action === 'delete') { out.push({ field: 'log', before: span(b.start_time, b.end_time), after: null }); return out; }
    const beforeDate = typeof b.start_time === 'string' ? displayDate(israelDate(b.start_time)) : '';
    const afterDate = typeof p.start_time === 'string' ? displayDate(israelDate(p.start_time)) : beforeDate;
    push('date', beforeDate, afterDate);
    push('start', israelHm(b.start_time), p.start_time ? israelHm(p.start_time) : israelHm(b.start_time));
    push('end', israelHm(b.end_time), p.end_time ? israelHm(p.end_time) : israelHm(b.end_time));
    if (typeof p.summary === 'string') push('summary', typeof b.summary === 'string' ? b.summary : '', p.summary);
    return out;
  }
  return out;
}
